'use client';

import type { IMessage } from '@ui/components/organisms/message/message.typings';

/**
 * Short-lived, module-scoped handoff of the exact clicked feed post to the post
 * detail route. One record only, 5-second TTL, consumed once after the route
 * commits. Never persisted: no browser storage, Redux state, or query cache.
 *
 * The opaque timing sample mirrors `apps/fan/src/lib/community-navigation-timing.ts`
 * conventions; it cannot be imported here because packages/ui cannot depend on
 * the fan app. It carries no route, entity, or user data.
 */

export const POST_DETAIL_HANDOFF_TTL_MS = 5_000;

export interface PostDetailHandoffRouteTokens {
  /** Exact creator token encoded in the post URL (display name or id). */
  creator: string;
  /** Exact conversation token encoded in the post URL. */
  conversation: string;
  /** Exact post token encoded in the post URL (slug or id). */
  post: string;
}

export interface PostNavigationSample {
  navigation_id: string;
  source: 'feed';
  started_at_ms: number;
}

export interface PostDetailHandoffRecord {
  message: IMessage;
  tokens: PostDetailHandoffRouteTokens;
  sample: PostNavigationSample;
  /** In-memory viewer binding; deliberately excluded from telemetry samples. */
  viewerId: string | null;
}

let handoffRecord: PostDetailHandoffRecord | null = null;
// Staging is the only event that needs to notify an already-mounted route. A
// route consumes the module record after taking its own snapshot, so clearing
// it must not invalidate that snapshot or make the route lose warm content.
let handoffStageRevision = 0;
const handoffStageListeners = new Set<() => void>();
export const POST_DETAIL_INTENT_CAP = 64;
const postDetailIntentKeys = new Set<string>();
const opaqueNavigationIdPattern =
  /^(?:[0-9a-f]{32}|[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12})$/i;

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.length > 0;

const normalizeViewerId = (viewerId: string | null | undefined): string | null =>
  isNonEmptyString(viewerId) ? viewerId : null;

/** Claims one capped post-intent prefetch key and evicts the oldest key at capacity. */
export const claimPostDetailPrefetchIntent = (intentKey: string): boolean => {
  if (!isNonEmptyString(intentKey) || postDetailIntentKeys.has(intentKey)) return false;
  postDetailIntentKeys.add(intentKey);
  if (postDetailIntentKeys.size > POST_DETAIL_INTENT_CAP) {
    const oldestIntentKey = postDetailIntentKeys.values().next().value;
    if (typeof oldestIntentKey === 'string') postDetailIntentKeys.delete(oldestIntentKey);
  }
  return true;
};

const areValidTokens = (tokens: unknown): tokens is PostDetailHandoffRouteTokens =>
  Boolean(
    tokens &&
      typeof tokens === 'object' &&
      isNonEmptyString((tokens as PostDetailHandoffRouteTokens).creator) &&
      isNonEmptyString((tokens as PostDetailHandoffRouteTokens).conversation) &&
      isNonEmptyString((tokens as PostDetailHandoffRouteTokens).post),
  );

// Legacy ?slug= routes can briefly hold literal 'undefined' URL segments while a
// redirect is pending; those tokens cannot be judged, so they never consume.
const areComparableTokens = (tokens: PostDetailHandoffRouteTokens): boolean =>
  tokens.creator !== 'undefined' &&
  tokens.conversation !== 'undefined' &&
  tokens.post !== 'undefined';

const createOpaqueNavigationId = (): string | null => {
  const webCrypto = globalThis.crypto;
  const navigationId = webCrypto?.randomUUID?.();
  return navigationId && opaqueNavigationIdPattern.test(navigationId) ? navigationId : null;
};

/** Creates an opaque click-time timing sample with no route or entity data. */
export const createPostNavigationSample = (
  startedAtMs = Date.now(),
): PostNavigationSample | null => {
  const navigation_id = createOpaqueNavigationId();
  if (!navigation_id || !Number.isSafeInteger(startedAtMs) || startedAtMs < 0) return null;
  return { navigation_id, source: 'feed', started_at_ms: startedAtMs };
};

/** True for an unmodified primary click (no new-tab/modified-key behavior). */
export const isUnmodifiedPrimaryClick = (event: {
  button?: number;
  metaKey?: boolean;
  ctrlKey?: boolean;
  shiftKey?: boolean;
  altKey?: boolean;
  defaultPrevented?: boolean;
}): boolean =>
  event.button === 0 &&
  !event.metaKey &&
  !event.ctrlKey &&
  !event.shiftKey &&
  !event.altKey &&
  !event.defaultPrevented;

/** Stages the single allowed handoff record, replacing any previous one. */
export const stagePostDetailHandoff = (
  message: IMessage,
  tokens: PostDetailHandoffRouteTokens,
  nowMs = Date.now(),
  viewerId?: string | null,
): boolean => {
  if (!message || !areValidTokens(tokens)) return false;
  const sample = createPostNavigationSample(nowMs);
  if (!sample) return false;
  handoffRecord = { message, tokens, sample, viewerId: normalizeViewerId(viewerId) };
  handoffStageRevision += 1;
  handoffStageListeners.forEach((listener) => listener());
  return true;
};

/**
 * A monotonic in-memory signal for consumers that are retained across an
 * exact same-URL navigation. It is deliberately not persisted or sent to
 * telemetry; it only lets a mounted route notice a fresh click-time handoff.
 */
export const getPostDetailHandoffStageRevision = (): number => handoffStageRevision;

/** Subscribes a retained post route to fresh click-time handoff stages only. */
export const subscribePostDetailHandoffStage = (listener: () => void): (() => void) => {
  handoffStageListeners.add(listener);
  return () => handoffStageListeners.delete(listener);
};

/**
 * Non-destructively returns the staged record only when every route token and
 * the post identity match exactly (case-sensitive) and the TTL has not expired.
 * A mismatched or expired record is cleared immediately.
 */
export const peekPostDetailHandoff = (
  tokens: PostDetailHandoffRouteTokens,
  messageId?: string | null,
  nowMs = Date.now(),
  viewerId?: string | null,
): PostDetailHandoffRecord | null => {
  const record = handoffRecord;
  if (!record) return null;

  // Unusable comparison inputs cannot judge the record, so they never consume it.
  if (!areValidTokens(tokens) || !areComparableTokens(tokens) || !messageId) return null;

  const expired =
    !Number.isFinite(nowMs) ||
    record.sample.started_at_ms > nowMs ||
    nowMs - record.sample.started_at_ms > POST_DETAIL_HANDOFF_TTL_MS;
  const matches =
    !expired &&
    record.tokens.creator === tokens.creator &&
    record.tokens.conversation === tokens.conversation &&
    record.tokens.post === tokens.post &&
    record.viewerId === normalizeViewerId(viewerId) &&
    (record.message.id === messageId || record.message.slug === messageId);

  if (!matches) {
    // Wrong route, stale identity, or expired: drop the single record.
    handoffRecord = null;
    return null;
  }

  // Kept until clearPostDetailHandoff consumes it once after the route commits,
  // so repeated peeks for the same route stay stable.
  return record;
};

/** Consumes the staged record exactly once after the target route commits. */
export const clearPostDetailHandoff = () => {
  handoffRecord = null;
};

/** Test-only escape hatch to reset module state between tests. */
export const resetPostDetailHandoffForTests = () => {
  handoffRecord = null;
  handoffStageRevision = 0;
  postDetailIntentKeys.clear();
};
